import React, { useState, useEffect } from "react";
import Button from "../../components/common/Button";
import Input from "../../components/form/input/InputField";
import api from "../../utills/api";

interface TextureCategory {
  _id: string;
  name: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface Texture {
  _id: string;
  textureId: string;
  textureCategory: string;
  textureUrl: string;
  textureType: string;
  textureName?: string;
  createdAt?: string;
  updatedAt?: string;
}

const TextureAdmin: React.FC = () => {
  const [categories, setCategories] = useState<TextureCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [newCategoryName, setNewCategoryName] = useState<string>("");
  const [newCategoryDescription, setNewCategoryDescription] = useState<string>("");
  const [editCategoryName, setEditCategoryName] = useState<string>("");
  const [editCategoryDescription, setEditCategoryDescription] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  // Texture states
  const [textures, setTextures] = useState<Texture[]>([]);
  const [isTextureModalOpen, setIsTextureModalOpen] = useState<boolean>(false);
  const [isTextureEditModalOpen, setIsTextureEditModalOpen] = useState<boolean>(false);
  const [isTextureDeleteModalOpen, setIsTextureDeleteModalOpen] = useState<boolean>(false);
  const [selectedTexture, setSelectedTexture] = useState<string>("");
  const [textureForm, setTextureForm] = useState({
    textureName: "",
    textureType: ""
  });
  const [textureFile, setTextureFile] = useState<File | null>(null);
  const [textureLoading, setTextureLoading] = useState<boolean>(false);
  const [textureError, setTextureError] = useState<string>("");

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch textures when category is selected
  useEffect(() => {
    if (selectedCategory) {
      setTextureError(""); // Clear any previous errors
      fetchTextures();
    } else {
      setTextures([]); // Clear textures when no category selected
      setTextureError(""); // Clear errors when no category selected
    }
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/api/texture-category');
      console.log(response.data);
      if (response.data && response.data.categories) {
        setCategories(response.data.categories);
        // Set first category as default selected
        if (response.data.categories.length > 0) {
          setSelectedCategory(response.data.categories[0]._id);
        }
      }
    } catch (error) {
      console.error("Error fetching texture categories:", error);
      setError("Failed to fetch texture categories");
    } finally {
      setLoading(false);
    }
  };

  const fetchTextures = async () => {
    try {
      setTextureLoading(true);
      const response = await api.get(`/admin/api/texture/category/${selectedCategory}`);
      console.log("Textures response:", response.data);
      if (response.data && response.data.success && response.data.textures) {
        setTextures(response.data.textures);
      } else if (response.data && !response.data.success) {
        // Handle case where no textures found for category
        setTextures([]);
        setTextureError(response.data.message || "No textures found for this category");
      }
    } catch (error: any) {
      console.error("Error fetching textures:", error);
      if (error.response?.status === 404) {
        setTextures([]);
        setTextureError("No textures found for this category");
      } else {
        setTextureError("Failed to fetch textures");
      }
    } finally {
      setTextureLoading(false);
    }
  };

  const handleAddCategory = () => {
    setIsModalOpen(true);
  };

  const handleEditCategory = () => {
    if (!selectedCategory) {
      setError("Please select a category to edit");
      return;
    }
    const category = categories.find(cat => cat._id === selectedCategory);
    if (category) {
      setEditCategoryName(category.name);
      setEditCategoryDescription(category.description || "");
      setIsEditModalOpen(true);
    }
  };

  const handleDeleteCategory = () => {
    if (!selectedCategory) {
      setError("Please select a category to delete");
      return;
    }
    setIsDeleteModalOpen(true);
  };

  // Texture handlers
  const handleAddTexture = () => {
    if (!selectedCategory) {
      setTextureError("Please select a category first");
      return;
    }
    setTextureForm({
      textureName: "",
      textureType: ""
    });
    setTextureFile(null);
    setIsTextureModalOpen(true);
  };

  const handleEditTexture = (texture: Texture) => {
    setSelectedTexture(texture._id);
    setTextureForm({
      textureName: texture.textureName || "",
      textureType: texture.textureType
    });
    setIsTextureEditModalOpen(true);
  };

  const handleDeleteTexture = (textureId: string) => {
    setSelectedTexture(textureId);
    setIsTextureDeleteModalOpen(true);
  };

  const handleCloseTextureModal = () => {
    setIsTextureModalOpen(false);
    setTextureForm({
      textureName: "",
      textureType: ""
    });
    setTextureFile(null);
    setTextureError("");
  };

  const handleCloseTextureEditModal = () => {
    setIsTextureEditModalOpen(false);
    setSelectedTexture("");
    setTextureForm({
      textureName: "",
      textureType: ""
    });
    setTextureError("");
  };

  const handleCloseTextureDeleteModal = () => {
    setIsTextureDeleteModalOpen(false);
    setSelectedTexture("");
    setTextureError("");
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setNewCategoryName("");
    setNewCategoryDescription("");
    setError("");
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditCategoryName("");
    setEditCategoryDescription("");
    setError("");
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setError("");
  };

  const handleSubmitCategory = async () => {
    if (!newCategoryName.trim()) {
      setError("Category name is required");
      return;
    }

    try {
      setLoading(true);
      setError("");
      
      const response = await api.post('/admin/api/texture-category/add', {
        name: newCategoryName.trim(),
        description: newCategoryDescription.trim()
      });

      if (response.data) {
        // Refresh categories list
        await fetchCategories();
        handleCloseModal();
      }
    } catch (error: any) {
      console.error("Error adding texture category:", error);
      setError(error.response?.data?.message || "Failed to add texture category");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitEditCategory = async () => {
    if (!editCategoryName.trim()) {
      setError("Category name is required");
      return;
    }

    try {
      setLoading(true);
      setError("");
      
      const response = await api.put(`/admin/api/texture-category/${selectedCategory}`, {
        name: editCategoryName.trim(),
        description: editCategoryDescription.trim()
      });

      if (response.data) {
        // Refresh categories list
        await fetchCategories();
        handleCloseEditModal();
      }
    } catch (error: any) {
      console.error("Error updating texture category:", error);
      setError(error.response?.data?.message || "Failed to update texture category");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitDeleteCategory = async () => {
    try {
      setLoading(true);
      setError("");
      
      const response = await api.delete(`/admin/api/texture-category/${selectedCategory}`);

      if (response.data) {
        // Refresh categories list
        await fetchCategories();
        setSelectedCategory("");
        handleCloseDeleteModal();
      }
    } catch (error: any) {
      console.error("Error deleting texture category:", error);
      setError(error.response?.data?.message || "Failed to delete texture category");
    } finally {
      setLoading(false);
    }
  };

   // Texture submit functions
   const handleSubmitTexture = async () => {
     if (!textureForm.textureName.trim() || !textureForm.textureType.trim()) {
       setTextureError("Texture name and type are required");
       return;
     }

     if (!textureFile) {
       setTextureError("Texture image is required");
       return;
     }

     try {
       setTextureLoading(true);
       setTextureError("");
       
       // Create FormData for file upload
       const formData = new FormData();
       
       // Add individual fields directly to FormData
       formData.append('textureName', textureForm.textureName.trim());
       formData.append('textureType', textureForm.textureType.trim());
       formData.append('textureCategory', selectedCategory);
       
       // Add file if it exists
       if (textureFile) {
         formData.append('image', textureFile);
       }
      
      const response = await api.post('/admin/api/texture/add', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data) {
        // Refresh textures list
        await fetchTextures();
        handleCloseTextureModal();
      }
    } catch (error: any) {
      console.error("Error adding texture:", error);
      setTextureError(error.response?.data?.message || "Failed to add texture");
    } finally {
      setTextureLoading(false);
    }
  };

   const handleSubmitEditTexture = async () => {
     if (!textureForm.textureName.trim() || !textureForm.textureType.trim()) {
       setTextureError("Texture name and type are required");
       return;
     }

     try {
       setTextureLoading(true);
       setTextureError("");
       
       // Create FormData for file upload
       const formData = new FormData();
       
       // Add individual fields directly to FormData
       formData.append('textureName', textureForm.textureName.trim());
       formData.append('textureType', textureForm.textureType.trim());
       formData.append('textureCategory', selectedCategory);
       
       // Add file if it exists
       if (textureFile) {
         formData.append('image', textureFile);
       }
      
      const response = await api.put(`/admin/api/texture/${selectedTexture}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data) {
        // Refresh textures list
        await fetchTextures();
        handleCloseTextureEditModal();
      }
    } catch (error: any) {
      console.error("Error updating texture:", error);
      setTextureError(error.response?.data?.message || "Failed to update texture");
    } finally {
      setTextureLoading(false);
    }
  };

  const handleSubmitDeleteTexture = async () => {
    try {
      setTextureLoading(true);
      setTextureError("");
      
      const response = await api.delete(`/admin/api/texture/${selectedTexture}`);

      if (response.data) {
        // Refresh textures list
        await fetchTextures();
        handleCloseTextureDeleteModal();
      }
    } catch (error: any) {
      console.error("Error deleting texture:", error);
      setTextureError(error.response?.data?.message || "Failed to delete texture");
    } finally {
      setTextureLoading(false);
    }
  };

  return (
    <div className="p-6 w-full max-w-full">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Texture Category Admin
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Manage texture categories
        </p>
      </div>

      {/* Category Selection Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Texture Categories
          </h2>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full h-11 rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2.5 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select a category</option>
              {categories.map(category => (
                <option key={category._id} value={category._id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex items-end gap-3">
            <Button
              onClick={handleAddCategory}
              variant="primary"
              size="md"
              startIcon={
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 5V19M5 12H19"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
            >
              Add Category
            </Button>
            <Button
              onClick={handleEditCategory}
              variant="secondary"
              size="md"
              disabled={!selectedCategory}
              startIcon={
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="m18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
            >
              Edit Category
            </Button>
            <Button
              onClick={handleDeleteCategory}
              variant="outline"
              size="md"
              disabled={!selectedCategory}
              startIcon={
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
            >
              Delete Category
            </Button>
          </div>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {loading && (
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm text-blue-600 dark:text-blue-400">Loading...</p>
          </div>
        )}
      </div>

      {/* Textures Section - Only show when category is selected */}
      {selectedCategory && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Textures
            </h2>
            <div className="flex items-center gap-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Category: {categories.find(cat => cat._id === selectedCategory)?.name}
              </p>
              <Button
                onClick={handleAddTexture}
                variant="primary"
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white border-0 shadow-sm"
                startIcon={
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 5V19M5 12H19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                }
              >
                Add Texture
              </Button>
            </div>
          </div>

          {textureLoading && (
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm text-blue-600 dark:text-blue-400">Loading textures...</p>
            </div>
          )}

          {textureError && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{textureError}</p>
            </div>
          )}

          {textures.length === 0 && !textureLoading ? (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400">No textures found for this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4">
              {textures.map((texture) => (
                <div key={texture._id} className="group bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200">
                  <div className="aspect-square bg-gray-100 dark:bg-gray-700 relative overflow-hidden">
                    {texture.textureUrl ? (
                      <img
                        src={texture.textureUrl}
                        alt={texture.textureName || "Texture"}
                        className="w-full h-full object-fit group-hover:scale-105 transition-transform duration-200"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    )}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <div className="bg-black/50 backdrop-blur-sm rounded-lg px-2 py-1">
                        <span className="text-white text-xs font-medium">{texture.textureType}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4">
                    <div className="mb-3">
                      <h3 className="font-semibold text-gray-900 dark:text-white text-sm mb-1 line-clamp-1">
                        {texture.textureName || "Unnamed Texture"}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {texture.textureType}
                      </p>
                    </div>
                    
                    <div className="flex gap-1">
                      <Button
                        onClick={() => handleEditTexture(texture)}
                        variant="outline"
                        size="sm"
                        className="text-xs h-7 px-2 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 flex-1 min-w-0"
                        startIcon={
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="m18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        }
                      >
                        Edit
                      </Button>
                      <Button
                        onClick={() => handleDeleteTexture(texture._id)}
                        variant="outline"
                        size="sm"
                        className="text-xs h-7 px-2 text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400 dark:text-red-400 dark:border-red-600 dark:hover:bg-red-900/20 flex-1 min-w-0"
                        startIcon={
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        }
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add Category Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Add New Texture Category
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Category Name *
              </label>
              <Input
                type="text"
                placeholder="Enter category name"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                error={!!error}
                hint={error}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                placeholder="Enter category description"
                value={newCategoryDescription}
                onChange={(e) => setNewCategoryDescription(e.target.value)}
                className="w-full h-24 rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2.5 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                size="md"
                onClick={handleCloseModal}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleSubmitCategory}
                disabled={loading || !newCategoryName.trim()}
                loading={loading}
              >
                Add Category
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Edit Texture Category
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Category Name *
              </label>
              <Input
                type="text"
                placeholder="Enter category name"
                value={editCategoryName}
                onChange={(e) => setEditCategoryName(e.target.value)}
                error={!!error}
                hint={error}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                placeholder="Enter category description"
                value={editCategoryDescription}
                onChange={(e) => setEditCategoryDescription(e.target.value)}
                className="w-full h-24 rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2.5 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                size="md"
                onClick={handleCloseEditModal}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleSubmitEditCategory}
                disabled={loading || !editCategoryName.trim()}
                loading={loading}
              >
                Update Category
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Category Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Delete Texture Category
            </h3>
            
            <div className="mb-4">
              <p className="text-gray-600 dark:text-gray-400">
                Are you sure you want to delete the category "{categories.find(cat => cat._id === selectedCategory)?.name}"? 
                This action cannot be undone.
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                size="md"
                onClick={handleCloseDeleteModal}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleSubmitDeleteCategory}
                disabled={loading}
                loading={loading}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete Category
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Texture Modal */}
      {isTextureModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 my-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Add New Texture
            </h3>
            
            <div className="space-y-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Texture Name *
                </label>
                <Input
                  type="text"
                  placeholder="Enter texture name"
                  value={textureForm.textureName}
                  onChange={(e) => setTextureForm({...textureForm, textureName: e.target.value})}
                  error={!!textureError}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Texture Type *
                </label>
                <Input
                  type="text"
                  placeholder="Enter texture type"
                  value={textureForm.textureType}
                  onChange={(e) => setTextureForm({...textureForm, textureType: e.target.value})}
                  error={!!textureError}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Texture Image *
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setTextureFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 dark:file:bg-gray-700 dark:file:text-gray-300 dark:hover:file:bg-gray-600"
                />
                {textureFile && (
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Selected: {textureFile.name}
                  </p>
                )}
              </div>
            </div>

            {textureError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{textureError}</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                size="md"
                onClick={handleCloseTextureModal}
                disabled={textureLoading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleSubmitTexture}
                disabled={textureLoading || !textureForm.textureName.trim() || !textureForm.textureType.trim() || !textureFile}
                loading={textureLoading}
              >
                Add Texture
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Texture Modal */}
      {isTextureEditModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 my-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Edit Texture
            </h3>
            
            <div className="space-y-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Texture Name *
                </label>
                <Input
                  type="text"
                  placeholder="Enter texture name"
                  value={textureForm.textureName}
                  onChange={(e) => setTextureForm({...textureForm, textureName: e.target.value})}
                  error={!!textureError}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Texture Type *
                </label>
                <Input
                  type="text"
                  placeholder="Enter texture type"
                  value={textureForm.textureType}
                  onChange={(e) => setTextureForm({...textureForm, textureType: e.target.value})}
                  error={!!textureError}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Current Image
                </label>
                {textures.find(t => t._id === selectedTexture)?.textureUrl && (
                  <div className="mb-3">
                    <img
                      src={textures.find(t => t._id === selectedTexture)?.textureUrl}
                      alt="Current texture"
                      className="w-32 h-32 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
                    />
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  New Texture Image (optional)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setTextureFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 dark:file:bg-gray-700 dark:file:text-gray-300 dark:hover:file:bg-gray-600"
                />
                {textureFile && (
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Selected: {textureFile.name}
                  </p>
                )}
              </div>
            </div>

            {textureError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{textureError}</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                size="md"
                onClick={handleCloseTextureEditModal}
                disabled={textureLoading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleSubmitEditTexture}
                disabled={textureLoading || !textureForm.textureName.trim() || !textureForm.textureType.trim()}
                loading={textureLoading}
              >
                Update Texture
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Texture Modal */}
      {isTextureDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Delete Texture
            </h3>
            
            <div className="mb-4">
              <p className="text-gray-600 dark:text-gray-400">
                Are you sure you want to delete this texture? This action cannot be undone.
              </p>
            </div>

            {textureError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{textureError}</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                size="md"
                onClick={handleCloseTextureDeleteModal}
                disabled={textureLoading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleSubmitDeleteTexture}
                disabled={textureLoading}
                loading={textureLoading}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete Texture
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TextureAdmin;
